import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faSquare } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from './utils/axiosinstance';

import Button from './shared/Button';
import PrivacyChoice from './PrivacyChoice';

// Temporarily disabled (2026-10-06): processing from the Create tab is off
// until the team is ready to take production GPU jobs. Flip to false to
// re-enable.
const PROCESSING_DISABLED = false;

const RunScail2 = ({ sourceVideo, sourceImage, onSuccess, onError }) => {
    const [isLoading, setIsLoading] = useState(false);
    // consent choice — private by default; sharing is an explicit opt-in
    const [shared, setShared] = useState(false);
    // background mode — neutral backdrop by default (strips more scene cues)
    const [restoreBg, setRestoreBg] = useState(false);
    // daily H100 budget: null = checking, true/false = whether this job fits
    const [budgetOk, setBudgetOk] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setBudgetOk(null);
        axiosInstance.get('/api/scail2_budget/', {
            params: {
                duration: sourceVideo?.duration || 10,
                restore_bg: restoreBg ? 1 : 0,
            },
        })
            .then((r) => { if (!cancelled) setBudgetOk(!!r.data.allowed); })
            .catch(() => { if (!cancelled) setBudgetOk(false); });
        return () => { cancelled = true; };
    }, [sourceVideo, restoreBg]);

    const unavailable = PROCESSING_DISABLED || budgetOk === false;

    const runScript = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post('/api/run-scail2/', {
                video_s3_key: sourceVideo.s3_key,
                image_s3_key: sourceImage.s3_key,
                shared: shared,
                restore_bg: restoreBg,
            });
            if (onSuccess) {
                onSuccess(response.data.video);
            }
        } catch (error) {
            // 429 = the daily GPU budget filled up since the page loaded
            if (error?.response?.status === 429) {
                setBudgetOk(false);
            } else if (onError) {
                onError(error);
            }
            console.error('SCAIL-2 error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const bgChoiceClasses = (active) =>
        `flex items-center gap-2 px-4 h-11 rounded-full border-2 transition-all cursor-pointer text-white ${
            active
                ? 'border-sil-yellow-500 bg-white bg-opacity-20 shadow'
                : 'border-white border-opacity-30 bg-white bg-opacity-5 hover:bg-opacity-10 text-opacity-70'
        }`;

    return (
        <div className="flex flex-col items-center gap-4">
            <PrivacyChoice shared={shared} onChange={setShared} />

            {/* background mode */}
            <div className="flex flex-wrap justify-center items-center gap-3">
                <button type="button" className={bgChoiceClasses(!restoreBg)}
                        onClick={() => setRestoreBg(false)}
                        title="Neutral backdrop — strips more scene clues (stronger anonymization)">
                    <FontAwesomeIcon icon={faSquare} />
                    <span className="text-sm font-semibold">Neutral background</span>
                </button>
                <button type="button" className={bgChoiceClasses(restoreBg)}
                        onClick={() => setRestoreBg(true)}
                        title="Keep the original video's background behind the anonymized person">
                    <FontAwesomeIcon icon={faImage} />
                    <span className="text-sm font-semibold">Original background (beta)</span>
                </button>
            </div>

            <Button
                onClick={runScript}
                loading={isLoading}
                disabled={unavailable || budgetOk === null}
            >
                Anonymize Video
            </Button>
            {unavailable && (
                <p className="text-white text-opacity-70 text-sm">
                    Video processing is temporarily unavailable.
                </p>
            )}
        </div>
    );
};

export default RunScail2;
